import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  Copy, 
  Check, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Sliders, 
  Layers, 
  Plus, 
  Trash2, 
  Compass, 
  Layout, 
  Code
} from 'lucide-react';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';

const GRADIENT_PRESETS = [
  { name: 'Sunset Silk', type: 'linear', angle: 135, stops: [{ id: 1, color: '#ff007f', position: 0 }, { id: 2, color: '#ff7b00', position: 100 }] },
  { name: 'Oceanic Glow', type: 'linear', angle: 135, stops: [{ id: 1, color: '#00c6ff', position: 0 }, { id: 2, color: '#0072ff', position: 100 }] },
  { name: 'Cyberpunk Neon', type: 'linear', angle: 45, stops: [{ id: 1, color: '#f107a3', position: 0 }, { id: 2, color: '#07f1ec', position: 100 }] },
  { name: 'Nordic Mint', type: 'linear', angle: 180, stops: [{ id: 1, color: '#11998e', position: 0 }, { id: 2, color: '#38ef7d', position: 100 }] },
  { name: 'Royal Purple', type: 'linear', angle: 90, stops: [{ id: 1, color: '#8e2de2', position: 0 }, { id: 2, color: '#4a00e0', position: 100 }] },
  { name: 'Magic Dust', type: 'linear', angle: 225, stops: [{ id: 1, color: '#a855f7', position: 0 }, { id: 2, color: '#ec4899', position: 50 }, { id: 3, color: '#3b82f6', position: 100 }] },
  { name: 'Gold Sand', type: 'linear', angle: 135, stops: [{ id: 1, color: '#ffe259', position: 0 }, { id: 2, color: '#ffa751', position: 100 }] },
  { name: 'Dark Void', type: 'linear', angle: 135, stops: [{ id: 1, color: '#141e30', position: 0 }, { id: 2, color: '#243b55', position: 100 }] }
];

export const GradientGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [type, setType] = useState('linear'); // linear, radial
  const [angle, setAngle] = useState(135); // 0 to 360
  const [stops, setStops] = useState([
    { id: 1, color: '#a855f7', position: 0 },
    { id: 2, color: '#06b6d4', position: 100 }
  ]);

  const [isCopiedCss, setIsCopiedCss] = useState(false);
  const [isCopiedTailwind, setIsCopiedTailwind] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  const canvasRef = useRef(null);

  // Helper to construct background CSS gradient property
  const getGradientCss = () => {
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    const stopString = sortedStops.map(s => `${s.color} ${s.position}%`).join(', ');
    
    if (type === 'linear') {
      return `linear-gradient(${angle}deg, ${stopString})`;
    } else {
      return `radial-gradient(circle, ${stopString})`;
    }
  };

  // Helper to generate tailwind arbitrary class value
  const getTailwindCode = () => {
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    if (type === 'linear') {
      return `bg-[linear-gradient(${angle}deg,${sortedStops.map(s => `${s.color}_${s.position}%`).join(',')})]`;
    } else {
      return `bg-[radial-gradient(circle,${sortedStops.map(s => `${s.color}_${s.position}%`).join(',')})]`;
    }
  };

  // Dynamic color stops modifiers
  const handleColorChange = (id, newColor) => {
    setStops(stops.map(s => s.id === id ? { ...s, color: newColor } : s));
  };

  const handlePositionChange = (id, newPos) => {
    setStops(stops.map(s => s.id === id ? { ...s, position: Math.max(0, Math.min(100, newPos)) } : s));
  };

  const addColorStop = () => {
    if (stops.length >= 6) {
      alert('Maximum of 6 color stops supported for clean design guidelines.');
      return;
    }
    // Calculate intermediate position between existing stops
    const nextId = Math.max(...stops.map(s => s.id)) + 1;
    // Generate a pleasant random color hex
    const randomHex = '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
    const midPos = Math.round(50);
    setStops([...stops, { id: nextId, color: randomHex, position: midPos }]);
  };

  const removeColorStop = (id) => {
    if (stops.length <= 2) {
      alert('Gradients require a minimum of 2 color stops to render correctly.');
      return;
    }
    setStops(stops.filter(s => s.id !== id));
  };

  // Surprise random gradient logic
  const handleRandomize = () => {
    const randomAngle = Math.floor(Math.random() * 36) * 10;
    const numStops = Math.random() > 0.7 ? 3 : 2;
    
    const generateColor = () => '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');

    const newStops = [];
    if (numStops === 2) {
      newStops.push({ id: 1, color: generateColor(), position: 0 });
      newStops.push({ id: 2, color: generateColor(), position: 100 });
    } else {
      newStops.push({ id: 1, color: generateColor(), position: 0 });
      newStops.push({ id: 2, color: generateColor(), position: 50 });
      newStops.push({ id: 3, color: generateColor(), position: 100 });
    }

    setAngle(randomAngle);
    setStops(newStops);
  };

  // Preset loading trigger
  const applyPreset = (preset) => {
    setType(preset.type);
    setAngle(preset.angle);
    setStops(preset.stops);
  };

  // Canvas background rendering download
  const handleDownloadImage = (width = 1920, height = 1080) => {
    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let gradient;
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);

    if (type === 'linear') {
      // Convert angle degrees to coordinate points inside canvas rectangles
      const angleRad = (angle - 90) * (Math.PI / 180);
      const r = Math.sqrt(width * width + height * height) / 2;
      const cx = width / 2;
      const cy = height / 2;

      const x0 = cx - r * Math.cos(angleRad);
      const y0 = cy - r * Math.sin(angleRad);
      const x1 = cx + r * Math.cos(angleRad);
      const y1 = cy + r * Math.sin(angleRad);

      gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    } else {
      // Center circles
      gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.max(width, height) / 2);
    }

    // Apply color values
    sortedStops.forEach(s => {
      gradient.addColorStop(s.position / 100, s.color);
    });

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Trigger download
    const link = document.createElement('a');
    link.download = `toolmetrico-gradient-${width}x${height}.png`;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsDownloaded(true);
    setTimeout(() => setIsDownloaded(false), 3000);
  };

  const copyToClipboard = (text, flagSetter) => {
    navigator.clipboard.writeText(text);
    flagSetter(true);
    setTimeout(() => flagSetter(false), 2000);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      
      {/* Hidden Download virtual canvas */}
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>

      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>CSS Gradient Generator</span>
      </div>



      {/* Main Container Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="gradient-workspace-grid">
        
        {/* Left Column: Color Stop Sliders & Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          {/* Stops Adjuster Card */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Layers size={18} style={{ color: 'var(--secondary)' }} />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Color Stop Coordinates</h3>
              </div>
              <button
                onClick={addColorStop}
                style={{
                  border: 'none',
                  background: 'rgba(14, 165, 233, 0.08)',
                  color: 'var(--secondary)',
                  fontWeight: 800,
                  fontSize: '0.8rem',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'var(--transition-smooth)'
                }}
              >
                <Plus size={14} /> Add Color Stop
              </button>
            </div>

            {/* List of active color stops */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {stops.map((stop, idx) => (
                <div
                  key={stop.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    padding: '12px 18px',
                    borderRadius: '12px'
                  }}
                >
                  {/* Hex Picker Box */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="color"
                      value={stop.color}
                      onChange={(e) => handleColorChange(stop.id, e.target.value)}
                      style={{
                        border: 'none',
                        outline: 'none',
                        width: '38px',
                        height: '38px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        padding: 0,
                        background: 'transparent'
                      }}
                    />
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, fontFamily: 'monospace', color: 'var(--text-primary)', width: '70px' }}>
                      {stop.color.toUpperCase()}
                    </span>
                  </div>

                  {/* Range Position Slider */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)' }}>Pos</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={stop.position}
                      onChange={(e) => handlePositionChange(stop.id, parseInt(e.target.value))}
                      style={{ flex: 1, accentColor: 'var(--secondary)', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.825rem', fontWeight: 800, color: 'var(--text-secondary)', width: '40px', textAlign: 'right' }}>
                      {stop.position}%
                    </span>
                  </div>

                  {/* Remove control */}
                  <button
                    onClick={() => removeColorStop(stop.id)}
                    disabled={stops.length <= 2}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: stops.length <= 2 ? 'var(--text-muted)' : '#ef4444',
                      cursor: stops.length <= 2 ? 'not-allowed' : 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Delete Stop"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

          </div>

          {/* Gradients Parameters config card */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Compass size={18} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Gradient Geometry</h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '24px'
            }}>
              {/* Type linear or radial */}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Gradient Layout Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {['linear', 'radial'].map((t) => (
                    <button
                      key={t}
                      onClick={() => setType(t)}
                      style={{
                        height: '38px',
                        border: type === t ? '1.5px solid var(--primary)' : '1px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        background: type === t ? 'rgba(5, 117, 251, 0.05)' : '#ffffff',
                        color: type === t ? 'var(--primary)' : 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        transition: 'var(--transition-smooth)'
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Linear Angle degree scale */}
              {type === 'linear' && (
                <div style={inputGroupStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={labelStyle}>Gradient Angle</label>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary)', background: 'rgba(5, 117, 251, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                      {angle}°
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="5"
                    value={angle}
                    onChange={(e) => setAngle(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    <span>0° (Vertical)</span>
                    <span>90° (Right)</span>
                    <span>180° (Down)</span>
                  </div>
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '8px 0' }} />

            {/* Quick Presets Gallery row */}
            <div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', display: 'block', marginBottom: '12px', letterSpacing: '0.04em' }}>
                Curated Designer Presets
              </span>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                gap: '10px'
              }}>
                {GRADIENT_PRESETS.map((p) => (
                  <button
                    key={p.name}
                    onClick={() => applyPreset(p)}
                    style={{
                      height: '74px',
                      borderRadius: '10px',
                      border: '1px solid var(--border-color)',
                      background: '#ffffff',
                      padding: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      textAlign: 'left',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="preset-btn-card"
                  >
                    {/* Tiny visual badge */}
                    <div style={{
                      height: '32px',
                      borderRadius: '6px',
                      width: '100%',
                      background: (() => {
                        const stopString = p.stops.map(s => `${s.color} ${s.position}%`).join(', ');
                        return `linear-gradient(${p.angle}deg, ${stopString})`;
                      })()
                    }} />
                    <span style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                      {p.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

          </div>

        </div>

        {/* Right Column: Visual Preview & Export blocks */}
        <div style={{
          position: 'sticky',
          top: '110px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          width: '100%',
          maxWidth: '420px',
          margin: '0 auto'
        }} className="gradient-preview-column">
          
          {/* Big Interactive Canvas Preview Card */}
          <div style={{
            background: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: 'var(--shadow-md)'
          }}>
            {/* Visual Canvas screen */}
            <div style={{
              background: getGradientCss(),
              borderRadius: '16px',
              aspectRatio: '16/10',
              width: '100%',
              boxShadow: '0 10px 25px -10px rgba(15, 23, 42, 0.15)',
              position: 'relative',
              overflow: 'hidden',
              marginBottom: '20px'
            }}>
              {/* Randomize Floating Sparkles trigger button */}
              <button
                onClick={handleRandomize}
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  right: '12px',
                  border: 'none',
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(4px)',
                  color: 'var(--text-primary)',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-md)',
                  transition: 'var(--transition-smooth)'
                }}
                className="randomize-float-btn"
                title="Random Surprise Gradient!"
              >
                <Sparkles size={16} />
              </button>
            </div>

            {/* Downloader assets options row */}
            <AdToast message={toast} />
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Download Image Wallpaper</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <button
                  onClick={withAd('gradient-hd', () => handleDownloadImage(1920, 1080))}
                  style={downloadBtnStyle}
                >
                  <Download size={14} /> Full HD (1080p)
                </button>
                <button
                  onClick={withAd('gradient-4k', () => handleDownloadImage(3840, 2160))}
                  style={{ ...downloadBtnStyle, border: '1px solid var(--secondary)', background: 'transparent', color: 'var(--secondary)' }}
                >
                  <Download size={14} /> Ultra HD (4K)
                </button>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '16px 0' }} />

            {/* Generated Code Boxes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* CSS Code box */}
              <div style={codeBoxWrapperStyle}>
                <div style={codeBoxHeaderStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-secondary)' }}>
                    <Code size={12} /> CSS STYLING PROPERTY
                  </div>
                  <button
                    onClick={() => copyToClipboard(`background: ${getGradientCss()};`, setIsCopiedCss)}
                    style={copyCodeBtnStyle}
                  >
                    {isCopiedCss ? <Check size={12} style={{ color: '#059669' }} /> : <Copy size={12} />}
                    {isCopiedCss ? 'Copied' : 'Copy Code'}
                  </button>
                </div>
                <div style={codeStringStyle}>
                  {`background: ${getGradientCss()};`}
                </div>
              </div>

              {/* Tailwind code box */}
              <div style={codeBoxWrapperStyle}>
                <div style={codeBoxHeaderStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-secondary)' }}>
                    <Code size={12} /> TAILWIND CLASS VALUE
                  </div>
                  <button
                    onClick={() => copyToClipboard(getTailwindCode(), setIsCopiedTailwind)}
                    style={copyCodeBtnStyle}
                  >
                    {isCopiedTailwind ? <Check size={12} style={{ color: '#059669' }} /> : <Copy size={12} />}
                    {isCopiedTailwind ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div style={{ ...codeStringStyle, wordBreak: 'break-all', whiteSpace: 'normal' }}>
                  {getTailwindCode()}
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>

      {/* Workspace dynamic CSS style tags */}
      <style>{`
        @media (min-width: 992px) {
          .gradient-workspace-grid {
            grid-template-columns: 1fr 420px !important;
          }
        }

        .preset-btn-card {
          transition: transform 0.2s, box-shadow 0.2s !important;
        }
        .preset-btn-card:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-sm) !important;
          border-color: var(--secondary) !important;
        }

        .randomize-float-btn:hover {
          transform: scale(1.1) rotate(15deg);
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="gradient-generator" />
    </div>
  );
};

// Internal static styles elements
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  boxShadow: 'var(--shadow-sm)'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const downloadBtnStyle = {
  background: 'linear-gradient(90deg, var(--secondary) 0%, var(--primary) 100%)',
  color: '#ffffff',
  border: 'none',
  borderRadius: '10px',
  height: '40px',
  fontSize: '0.825rem',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  transition: 'var(--transition-smooth)'
};

const codeBoxWrapperStyle = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '10px',
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const codeBoxHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const copyCodeBtnStyle = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontSize: '0.72rem',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

const codeStringStyle = {
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  color: 'var(--text-primary)',
  background: '#ffffff',
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid var(--border-color)',
  whiteSpace: 'nowrap',
  overflowX: 'auto'
};

export default GradientGenerator;
